import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, FileText, Truck, CheckCircle, X } from 'lucide-react';
import { purchaseOrderService } from '../api/mockRepo';
import { vendorService } from '../api/mockRepo';
import { grnService } from '../api/mockRepo';
import type { PurchaseOrder, Vendor } from '../types';
import { z } from 'zod';
import toast from 'react-hot-toast';

const poSchema = z.object({
  vendor_id: z.string().min(1, 'Vendor is required'),
  order_date: z.string().min(1, 'Order date is required'),
  expected_delivery_date: z.string().optional(),
  status: z.enum(['draft', 'sent', 'acknowledged', 'partially_received', 'completed', 'cancelled']),
  subtotal: z.number().min(0, 'Subtotal must be positive'),
  tax_amount: z.number().min(0, 'Tax amount must be positive'),
  total: z.number().min(0, 'Total must be positive'),
  notes: z.string().optional()
});

type POFormData = z.infer<typeof poSchema>;

export default function PurchaseOrders() {
  const { formatCurrency } = useCurrency();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<POFormData>({
    resolver: zodResolver(poSchema),
    defaultValues: {
      status: 'draft',
      tax_amount: 0
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [poData, vendorData] = await Promise.all([
        purchaseOrderService.getAll(),
        vendorService.getAll()
      ]);
      setPurchaseOrders(poData);
      setVendors(vendorData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };


  const onSubmit = async (data: POFormData) => {
    try {
      if (editingPO) {
        const updated = await purchaseOrderService.update(editingPO.id, data);
        setPurchaseOrders(purchaseOrders.map(po => po.id === editingPO.id ? updated : po) as PurchaseOrder[]);
        toast.success('Purchase order updated successfully');
      } else {
        const created = await purchaseOrderService.create({
          ...data,
          items: []
        } as any);
        setPurchaseOrders([...purchaseOrders, created]);
        toast.success('Purchase order created successfully');
      }
      
      setShowForm(false);
      setEditingPO(null);
      reset();
    } catch (error) {
      console.error('Failed to save purchase order:', error);
      toast.error('Failed to save purchase order');
    }
  };

  const handleEdit = (po: PurchaseOrder) => {
    setEditingPO(po);
    reset({
      vendor_id: po.vendor_id,
      order_date: po.order_date,
      expected_delivery_date: po.expected_delivery_date || '',
      status: po.status,
      subtotal: po.subtotal,
      tax_amount: po.tax_amount,
      total: po.total,
      notes: po.notes || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPO(null);
    reset();
  };

  const handleReceive = async (po: PurchaseOrder) => {
    try {
      const grnData = {
        po_id: po.id,
        vendor_id: po.vendor_id,
        received_date: new Date().toISOString().split('T')[0],
        received_by: 'Current User',
        status: 'completed' as const,
        notes: `GRN for PO ${po.po_number}`,
        items: po.items.map(item => ({
          po_item_id: item.id,
          item_id: item.item_id,
          quantity_received: item.quantity_ordered,
          condition: 'good' as const,
          notes: 'Received in good condition'
        }))
      };
      
      await grnService.create(grnData as any);
      
      const updatedPO = await purchaseOrderService.update(po.id, { 
        status: 'completed' 
      });
      setPurchaseOrders(purchaseOrders.map(p => p.id === po.id ? updatedPO : p) as PurchaseOrder[]);
      
      toast.success('Goods received successfully. Stock updated.');
    } catch (error) {
      console.error('Failed to receive goods:', error);
      toast.error('Failed to receive goods');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'partially_received': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return Truck;
      case 'completed': return CheckCircle;
      default: return FileText;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Orders</h1>
          <p className="text-sm text-neutral-600">Create and track purchase orders</p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New PO
        </button>
      </header>

      {showForm && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {editingPO ? 'Edit Purchase Order' : 'New Purchase Order'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor *
                </label>
                <select
                  {...register('vendor_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Vendor</option>
                  {vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name} - {vendor.vendor_code}
                    </option>
                  ))}
                </select>
                {errors.vendor_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.vendor_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Date *
                </label>
                <input
                  type="date"
                  {...register('order_date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.order_date && (
                  <p className="text-sm text-red-600 mt-1">{errors.order_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  {...register('expected_delivery_date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="partially_received">Partially Received</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtotal *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('subtotal', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.subtotal && (
                  <p className="text-sm text-red-600 mt-1">{errors.subtotal.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('tax_amount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('total', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.total && (
                  <p className="text-sm text-red-600 mt-1">{errors.total.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingPO ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PO Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchaseOrders.map((po) => {
                const StatusIcon = getStatusIcon(po.status);
                return (
                  <tr key={po.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <StatusIcon className="h-5 w-5 text-blue-500 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {po.po_number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {po.order_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {po.expected_delivery_date || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${po.total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(po.status)}`}>
                        {po.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleEdit(po)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleReceive(po)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Receive
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
